import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * SwipeBack — wraps a route's content with an iOS-style edge-drag-back gesture.
 *
 * Behaviour:
 *   • Pointer-down within EDGE_ZONE px from the left edge starts the gesture.
 *   • While dragging, the content is translated horizontally (translate3d) and
 *     a dim overlay fades in behind it for depth.
 *   • Releasing past COMMIT_THRESHOLD of the viewport width OR at high velocity
 *     commits the back action (`navigate(-1)` by default).
 *   • Otherwise the content snaps back via a short CSS transition.
 *
 * Constraints kept intentional:
 *   • Only horizontal drags qualify — we abort on the first noticeable vertical
 *     movement so scrolling never feels hijacked.
 *   • Uses Pointer Events so it works for touch, pen, and trackpad drag.
 *   • No external animation libs; everything is plain CSS transforms.
 */

const EDGE_ZONE = 30;            // px from the left edge that activates the gesture
const COMMIT_THRESHOLD = 0.35;   // fraction of viewport width to commit
const VELOCITY_COMMIT = 0.6;     // px/ms threshold for "flick" commit
const SNAP_DURATION = 220;       // ms for snap-back / commit animation

const SwipeBack = ({ children, onBack, className = '' }) => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const dragRef = useRef(null); // mutable state during a drag without re-renders
  const [animating, setAnimating] = useState(false);

  const resolveBack = useCallback(() => {
    if (typeof onBack === 'function') {
      onBack();
    } else {
      navigate(-1);
    }
  }, [onBack, navigate]);

  const applyTransform = useCallback((dx, withTransition) => {
    const el = containerRef.current;
    if (!el) return;
    el.style.transition = withTransition ? `transform ${SNAP_DURATION}ms ease-out` : 'none';
    el.style.transform = `translate3d(${Math.max(dx, 0)}px, 0, 0)`;
  }, []);

  const cleanup = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    el.style.transition = '';
    el.style.transform = '';
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    const handlePointerDown = (event) => {
      // Only primary touches / left-click drags from the left edge.
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      const x = event.clientX;
      if (x > EDGE_ZONE) return;

      dragRef.current = {
        pointerId: event.pointerId,
        startX: x,
        startY: event.clientY,
        startTime: performance.now(),
        lastX: x,
        lastTime: performance.now(),
        active: false,           // becomes true once horizontal threshold is crossed
        cancelled: false,
      };
    };

    const handlePointerMove = (event) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId || drag.cancelled) return;
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;

      // Decide whether this gesture is horizontal — only once.
      if (!drag.active) {
        if (Math.abs(dy) > 12 && Math.abs(dy) > Math.abs(dx)) {
          // Vertical scroll — abandon.
          drag.cancelled = true;
          return;
        }
        if (dx > 10) {
          drag.active = true;
          // Capture so we keep receiving pointermove even if pointer leaves.
          try { el.setPointerCapture(event.pointerId); } catch { /* noop */ }
        } else {
          return;
        }
      }

      // Track velocity from the most recent sample.
      drag.lastX = event.clientX;
      drag.lastTime = performance.now();

      applyTransform(dx, false);
      // Prevent page from also scrolling horizontally during the drag.
      if (event.cancelable) event.preventDefault();
    };

    const handlePointerEnd = (event) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      dragRef.current = null;

      if (!drag.active || drag.cancelled) {
        cleanup();
        return;
      }

      const dx = event.clientX - drag.startX;
      const dt = Math.max(performance.now() - drag.lastTime, 1);
      const recentVelocity = (event.clientX - drag.lastX) / dt; // px per ms, signed
      const width = window.innerWidth || 0;
      const shouldCommit =
        dx > width * COMMIT_THRESHOLD || (dx > 60 && recentVelocity > VELOCITY_COMMIT);

      setAnimating(true);
      if (shouldCommit) {
        applyTransform(width, true);
        window.setTimeout(() => {
          setAnimating(false);
          cleanup();
          resolveBack();
        }, SNAP_DURATION);
      } else {
        applyTransform(0, true);
        window.setTimeout(() => {
          setAnimating(false);
          cleanup();
        }, SNAP_DURATION);
      }
    };

    el.addEventListener('pointerdown', handlePointerDown, { passive: true });
    el.addEventListener('pointermove', handlePointerMove, { passive: false });
    el.addEventListener('pointerup', handlePointerEnd, { passive: true });
    el.addEventListener('pointercancel', handlePointerEnd, { passive: true });
    return () => {
      el.removeEventListener('pointerdown', handlePointerDown);
      el.removeEventListener('pointermove', handlePointerMove);
      el.removeEventListener('pointerup', handlePointerEnd);
      el.removeEventListener('pointercancel', handlePointerEnd);
    };
  }, [applyTransform, cleanup, resolveBack]);

  return (
    <div
      ref={containerRef}
      // touch-action: pan-y keeps vertical scrolling responsive while letting
      // us own horizontal pans for the swipe-back gesture.
      style={{ touchAction: 'pan-y', willChange: animating ? 'transform' : 'auto' }}
      className={className}
      data-swipeback-active={animating ? 'true' : 'false'}
    >
      {children}
    </div>
  );
};

export default SwipeBack;
